import { GridFilterItem } from '@mui/x-data-grid-pro';

import {
  AttachmentTypesResponse,
  AttendanceReportListResponse,
  BulkReportResponse,
  NewReportDataType,
  OverwriteReportDataType,
  ReportAccessData,
  ReportAccessResponse,
  ReportResponse,
  ReportScheduleEmailType,
  ReportTestEmailType,
  SavedReportDataType,
  SavedReportsListResponse,
} from '../@types';
import { httpClient, HttpMethod } from './http-client';
import { CustomFilterSettings, QueryOption } from '../@types/common.type';

// Response type for test email
interface TestEmailResponse {
  success: boolean;
  message: string;
}

// Response type for delete operation
interface DeleteReportResponse {
  id: number;
  success?: boolean;
}

// Response type for bulk update
interface BulkUpdateResponse {
  success: boolean;
  updated: number;
}

export const getAttendanceReports = (queryOptions: QueryOption, customFilter?: CustomFilterSettings): Promise<AttendanceReportListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/attendance?page=${page + 1}&limit=${pageSize}&includeChilds=${customFilter?.includeChilds}&startDate=${customFilter?.startDate}&endDate=${customFilter?.endDate}`;

  if (customFilter?.locationId) {
    url += `&locationId=${customFilter?.locationId}`;
  }

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpClient<AttendanceReportListResponse>(url, HttpMethod.Get);
};

export const getSavedReportsByCreatedId = (queryOptions: QueryOption): Promise<SavedReportsListResponse> => {
  const { filterModel, page, pageSize, sortModel } = queryOptions;
  let url = `/savedReport?limit=${pageSize}&page=${page + 1}`;

  if (sortModel && sortModel[0] && sortModel[0].field) {
    url += `&order_by=${sortModel[0].field}&order_direction=${sortModel[0].sort}`;
  }

  filterModel.items?.forEach((filter: GridFilterItem) => {
    const field = filter?.field;
    const operator = filter?.operator;
    const value = filter?.value ?? '';
    url += `&${field}=${operator},${value}`;
  });

  return httpClient<SavedReportsListResponse>(url, HttpMethod.Get);
};

export const createReport = (report: NewReportDataType): Promise<ReportResponse> => {
  return httpClient<ReportResponse>('/savedReport', HttpMethod.Post, report);
};

export const getReportByCode = (code: string): Promise<SavedReportDataType> => {
  return httpClient<SavedReportDataType>(`/savedReport/${code}`, HttpMethod.Get);
};

export const updateReportByCode = (code: string, report: OverwriteReportDataType): Promise<ReportResponse> => {
  return httpClient<ReportResponse>(`/savedReport/${code}`, HttpMethod.Put, report);
};

export const createSavedReportAccess = (id: number, reportAccessData: ReportAccessData): Promise<ReportAccessData> => {
  return httpClient<ReportAccessData>(`/savedReport/${id}/shareAccess`, HttpMethod.Post, reportAccessData);
};

export const updateReportAccessDataById = (Id: string, reportAccessData: ReportAccessData): Promise<ReportAccessData> => {
  return httpClient<ReportAccessData>(`/savedReportAccess/${Id}`, HttpMethod.Put, reportAccessData);
};

export const getSavedReportAccessByReportId = (reportId: string): Promise<ReportAccessResponse> => {
  return httpClient<ReportAccessResponse>(`/savedReportAccess/${reportId}`, HttpMethod.Get);
};

export const getReportAccessByReportCode = (code: string): Promise<ReportAccessData> => {
  return httpClient<ReportAccessData>(`/savedReport/${code}/viewAccess`, HttpMethod.Get);
};

export const getAttachmentTypes = (): Promise<AttachmentTypesResponse> => {
  return httpClient<AttachmentTypesResponse>('/attachmentType', HttpMethod.Get);
};

export const createScheduleReport = (reportData: ReportScheduleEmailType): Promise<ReportScheduleEmailType> => {
  return httpClient<ReportScheduleEmailType>('/scheduleReport', HttpMethod.Post, reportData);
};

export const getReportScheduleDataByReportId = (reportId: string): Promise<ReportScheduleEmailType> => {
  return httpClient<ReportScheduleEmailType>(`/scheduleReport/getByReportId/${reportId}`, HttpMethod.Get);
};

export const updateReportScheduleEmailByReprtId = (reportId: string, updatedScheduleEmailData: ReportScheduleEmailType): Promise<ReportScheduleEmailType> => {
  return httpClient<ReportScheduleEmailType>(`/scheduleReport/${reportId}`, HttpMethod.Put, updatedScheduleEmailData);
};

export const getDefaultReportByReportType = (reportTypeName: string): Promise<SavedReportDataType[]> => {
  return httpClient<SavedReportDataType[]>(`/savedReport/${reportTypeName}/isDefault`, HttpMethod.Get);
};

export const updateBulkReports = (reports: BulkReportResponse[]): Promise<BulkUpdateResponse> => {
  return httpClient<BulkUpdateResponse>('/defaultReport', HttpMethod.Patch, reports);
};

export const deleteReportById = (id: number): Promise<DeleteReportResponse> => {
  return httpClient<DeleteReportResponse>(`/savedReport/${id}`, HttpMethod.Delete);
};

export const sendSavedReportTestEmail = (testEmailData: ReportTestEmailType): Promise<TestEmailResponse> => {
  return httpClient<TestEmailResponse>('/savedReport/testEmail', HttpMethod.Post, testEmailData);
};